import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { settleRoster, sessionGone } from '../agents/settleRoster'
import { hasChatv1Token } from '../chatv1/token'
import { refreshChatv1Token } from '../chatv1/auth'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  vaultOpen,
  agentKill,
  terminalClose,
  getNoteCounts,
  getGroupMembers,
  getProjects,
  getAccountDevices,
  type AccountDevice,
  type AgentRow,
  type ProjectRow,
  type SecurityStatus,
} from '../transport/api'
import { onStreamOpen } from '../servers/commandStream'
import { useAnswerRetry } from '../agents/useAnswerRetry'
import { AgentRowView, type AgentRowCtx } from './AgentRowView'
import { Icon } from '../ui/Icon'
import { PlusLaunchpad } from './PlusLaunchpad'
import { PinnedSection, pinSectionId } from './PinnedSection'
import { PinTagSheet } from './PinTagSheet'
import { LastSpeakerFace } from './LastSpeakerFace'
import { ProjectAvatar } from '../identity/ProjectAvatar'
import { getAgentQueues } from '../transport/api'
import { displayMachineName, machineNameOf } from '../agents/machineName'
import { getAgentsEverywhere, mergeEverywhere, type Everywhere } from '../agents/everywhere'
import { relayTarget } from '../transport/relay'
import { useSections } from './useSections'
import { SidebarSection as Section } from './SidebarSection'
import { choosePreview, type Preview } from './previewChoice'
import { fetchSession } from '../auth/googleSession'
import { usePins, pinKey, tagOf, targetOfAgent, type Pin, type PinTarget } from './usePins'
import { FriendsTab } from '../friends/FriendsTab'
import { blockedByAgent } from '../agents/blockedAgents'
import { presenceFor, switchRevision, useIdleStatus } from '../agents/useIdleStatus'
import { useQueueSignal } from '../agents/QueueGroup'
import { idleKey } from '../agents/everywhere'
import { loadPreviews, savePreviews } from './previewCache'
import type { Selection } from '../urlState'

/**
 * #sbList — two-level nested browsing, per docs/AGENTS-SIDEBAR-GROUP-NESTING.md.
 *
 * Locked sort order from that spec:
 *   1. Group section (only when status.group is set AND unlocked)
 *   2. Projects
 *   3. Machines A→Z, by the name each one SHOWS
 *   within a machine: session name A→Z
 *
 * The spec put the local machine above the rest, and that rank has been dropped:
 * with paired devices there is no local one, only the one the relay happens to point
 * at, and ranking on that made the rail reshuffle on its own. See the sort itself.
 *
 * Collapse state persists under `wt_sb_sections` (see useSections).
 */

/**
 * WHICH ROW IS ARMED, and which React row this is — one definition, because they are
 * compared against each other.
 *
 * Keyed by MACHINE as well as name: one session name really does exist on two machines
 * on this account, and a key without the machine arms both rows on one tap. It was also
 * computed in two places with two different spellings, and changing one of them would
 * have left a delete button that could never reach its second tap.
 */
function termKey(t: { session: string; host: string | null; device_id?: string | undefined }): string {
  return `${t.device_id ?? ''}\0${t.host ?? ''}\0${t.session}`
}

export function Sidebar({
  status,
  openProjectId,
  onOpenProject,
  onDragSel,
  openAgentHash,
  openAgentDevice,
  onOpenAgent,
  openDmPeer,
  onOpenDm,
  onOpenShared,
  openSharedId = null,
  onOpenCard,
  onOpenMachineShell,
  dmUnreadByEmail,
  dmPreviewByEmail,
  filter = '',
  contentHits,
  scope = 'home',
  localName = null,
  only,
  openGroupId = null,
  onGroupOpened,
}: {
  status: SecurityStatus | undefined
  /**
   * What this machine calls itself, for the heading over its own agents.
   *
   * `Origin` is the edge's internal word for "not a mounted remote" (`host === null`)
   * and it was reaching the screen as a heading — a RELATIONSHIP, not a machine, so
   * someone with two paired machines reads "Origin" above both and has nothing to
   * tell them apart.
   *
   * A prop rather than a query here: the caller already holds the config, and a
   * second `useQuery` for it would be a second thing to mock in every test that
   * renders this — which is exactly what it was, until they all went red at once.
   */
  localName?: string | null
  openProjectId: string | null
  onOpenProject: (p: ProjectRow) => void
  /**
   * Begin (or end, with null) dragging a conversation onto the split stage.
   *
   * OPTIONAL, and its absence is what turns dragging off: a pane iframe renders no rail
   * and a narrow screen has nowhere to drop, so the parent simply does not pass it. A
   * row that can be lifted and cannot be landed is worse than one that does not lift.
   */
  onDragSel?: (sel: Selection | null) => void
  openAgentHash: string | null
  /** The open agent's machine — paired with the hash so a same-named agent on a
   *  second machine (same hash) does not light up alongside the one that is open.
   *  Optional so the many test mounts that never open an agent need not pass it. */
  /** ＋ → Group just made this one: the Humans tab's Groups list opens it (groups/GroupsSection). */
  openGroupId?: string | null
  onGroupOpened?: () => void
  openAgentDevice?: string | null
  onOpenAgent: (a: AgentRow) => void
  openDmPeer: string | null
  /** Search text. Names are matched here; content matches arrive in contentHits. */
  filter?: string
  /**
   * Channel hashes whose terminal output matched, from /agent/search.
   *
   * Kept separate from the name match so a row can say WHY it is here. An agent
   * whose name has nothing to do with the query, appearing with no explanation,
   * reads as a broken filter rather than as a content hit.
   */
  contentHits?: Set<string>
  /**
   * When present, the list is THIS and nothing else — the global search's results.
   * Owner, 2026-09-29: 搜尋之後呢, 我們只需要呈現所有從 Edge 端傳過來的資訊就好了, 那麼
   * 原本 Side Panel 上面的 Agent、Pinned 還有不同機器的這些欄位就都可以先取消了.
   *
   * Every query above keeps running while it is shown, so the ✕ that takes it away
   * puts the sections back as they were, with nothing to reload.
   */
  only?: ReactNode
  /**
   * Which nav tab the rail is showing.
   *
   * 'home' is everything, unchanged — the owner's rule is that Home IS the current
   * screen. 'group' narrows to the people section, which is what the Group tab
   * means here: this app's DM peers are the members of the trust domain, so a
   * "DMs" tab and a "Group" tab would have listed the same rows under two names.
   */
  scope?: 'home' | 'group'
  onOpenDm: (email: string, account?: string) => void
  /** A friend's agent, from the Humans tab's dropdown: it opens on the stage like one of yours. */
  onOpenShared?: (shareId: string) => void
  /** The friend's agent on the stage now, for its row's highlight. */
  openSharedId?: string | null
  /** The agent's face was tapped: open its card. Optional so the group scope and the
   *  tests that only exercise the roster do not have to supply one. */
  onOpenCard?: (a: AgentRow) => void
  /** The machine section's Terminal button: pop a non-tmux shell ON that device,
   *  at its home folder (owner, 2026-08-27). Absent = no button. */
  onOpenMachineShell?: (device: string, name: string) => void
  /** email → unread DMs, for the marks on the friend rows. Passed through rather than
   *  fetched here so the Humans TAB and the rows under it count the same list. */
  dmUnreadByEmail?: Map<string, number>
  /** email → the DM's last-message preview, for the friend cards' strip. */
  dmPreviewByEmail?: Map<string, import('../chat/dmUnread').DmPreview>
}) {
  const { isOpen, toggle } = useSections()
  const { pins, tags: pinTags, isPinned, moveTo: movePin } = usePins()
  /* The pin sheet: for an agent whose pin was pressed, or for a tag whose heading's
     pencil was (owner, 2026-10-12 — see PinTagSheet). One at a time, so one state. */
  const [pinSheet, setPinSheet] = useState<{ target: PinTarget } | { editTag: string } | null>(null)
  const closePinSheet = useCallback(() => setPinSheet(null), [])
  const qc = useQueryClient()
  /*
   * Creating a project moved into the ＋ sheet's Project tab. It asked for one thing —
   * a name — and had a button, an inline form and an error line in the rail to ask it,
   * next to a ＋ that already means "make something". One entry point now.
   */
  // Real presence (the sidebar's dots were fake — onboarded≠active). One shared 5s poll.
  const idle = useIdleStatus()
  /* The account's own face, for the line that quotes the account's own words. Same
     query key and fetcher App uses, so this reads its cache rather than asking again. */
  const me = useQuery({ queryKey: ['auth-me'], queryFn: fetchSession, staleTime: Infinity })
  const myFace = useMemo(
    () => (me.data?.kind === 'in' ? { picture: me.data.identity.picture, email: me.data.identity.email } : null),
    [me.data],
  )
  const hasDrag = !!onDragSel
  const hasCard = !!onOpenCard
  const unlocked = vaultOpen(status)
  const groupName = status?.group?.name ?? null

  const agents = useQuery({
    queryKey: ['agents'],
    /*
     * EACH MACHINE'S ROWS LAND AS THAT MACHINE ANSWERS — the fourth surface to
     * shed the whole-fleet lump (after the @ gate, the busy dot and the attach
     * sheet). The lump is why create/delete felt ten seconds slow (owner,
     * 2026-08-30): the acting machine answers in under a second and its row
     * change sat in a sweep waiting for the machines that did not act. The
     * relay's machine is asked first; the resolved sweep still replaces the
     * cache at the end, so machines that stopped answering drop out then.
     */
    queryFn: () =>
      getAgentsEverywhere(null, {
        first: relayTarget(),
        onMachine: (m) => {
          qc.setQueryData<Everywhere>(['agents'], (prev) => mergeEverywhere(prev, m))
        },
      }),
    // A rail drawn from a cached roster while the app was in the background is the
    // stale half of what the owner reported; `staleTime: 0` lets the refetch below
    // actually go out instead of being deduped as fresh enough.
    staleTime: 0,
  })

  /*
   * Ask again when the app comes back.
   *
   * `refetchOnWindowFocus` is on globally and was MEASURED firing nothing —
   * zero requests in the 700ms after a visibilitychange (see useIdleStatus, which
   * carries the same listener for the same reason). The status half had this and the
   * roster half did not, so coming back to the app could leave a machine's agents
   * listed exactly as they were when it went away.
   */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void agents.refetch()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
   * ASK AGAIN WHEN A MACHINE DID NOT ANSWER — owner, 2026-08-20: 大多數的時候看到
   * 這個紅字, 我 refresh browser 就可以解決了. 既然系統都知道沒連上, 可以讓系統
   * 自己 retry 嗎?
   *
   * It can, and the refresh is the clue to WHY it works: reloading re-opens the
   * account socket, and this fan-out fails per-machine when that socket was not
   * carrying answers yet. So the fix is not a poll — it is asking again on the
   * signal that the connection is back, plus a few backed-off attempts for the
   * blips that resolve on their own.
   *
   * NOT every 0.5s, deliberately. Each attempt is a real relay request to a
   * machine that may be legitimately off, so a permanent half-second poll would
   * bill every machine on the account forever for a message that is, by then,
   * TRUE. Three tries over ~8s covers the transient; after that the sentence on
   * screen is the honest one and should be allowed to stand.
   *
   * The query itself SUCCEEDS in this case and reports the failure inside its
   * data (`failed[]`), which is why react-query's own retry never applied here.
   */
  /*
   * Re-ask a machine that has not answered — bounded ladder + reconnect. The rail
   * shares this with the stage (App) now: the same fault stranded the stage on a
   * phone, where the rail is unmounted. See useAnswerRetry.
   */
  useAnswerRetry(
    agents.data?.failed?.length ?? 0,
    agents.dataUpdatedAt,
    () => void agents.refetch(),
  )
  /*
   * And when the stream reopens, pull any pin set on another device (its server
   * write poked us). The agents re-ask is the hook's; this is the rail's own.
   */
  useEffect(
    () => onStreamOpen(() => void qc.invalidateQueries({ queryKey: ['account-pins'] })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  /**
   * Pending notes per agent, so a queue that grew while you were looking
   * elsewhere is still visible. Degrades to nothing: a 404 means this deployment
   * has no counts endpoint yet, which is a fact about the server rather than a
   * failure, so it must not retry or surface an error — the rows simply carry no
   * badge until it exists.
   */
  /**
   * Messages held for each agent, so "who has mail waiting" is answerable from
   * wherever you are.
   *
   * This started life only inside Clean View, at the owner's placement — and that
   * made it visible exactly where you are not: answering an agent's prompt means
   * switching to its terminal, and a queue drains about a second after the prompt
   * clears. The window to see it was the block itself, from a view you had to leave.
   *
   * The rail already lists every agent, so the count belongs on the row. Not vault-
   * gated (unlike note counts) because /agent/* sits outside that gate — a locked
   * vault must not make queued mail look like no queued mail.
   */
  const queues = useQuery({
    queryKey: ['agent-queues'],
    queryFn: getAgentQueues,
  })
  // The rail badge shares the bell's fate: without the edge's queue_changed
  // signal it only moved on focus. One subscription here covers every row.
  useQueueSignal()
  const queuedFor = (a: AgentRow) =>
    queues.data?.queues.find(
      // `||`, not `??`: the edge keys a local queue by the EMPTY STRING and the roster says
      // null — both mean "this machine". With `??` the two never matched and this badge
      // never appeared for mail held on the machine itself.
      //
      // AND ONLY ON THE MACHINE WHOSE QUEUE THIS IS. The queue on screen is one machine's —
      // the one the screen points at (the switch handler empties it when that changes) —
      // while the rail lists every machine's agents. Without the machine in the match the
      // badge sat on every agent of that name on every machine. A row with no device_id is
      // "whichever machine the relay points at" (AgentRow), which is this one.
      (q) =>
        q.to_session === a.session &&
        (q.to_host || null) === (a.host || null) &&
        (!a.device_id || a.device_id === relayTarget()),
    )?.depth ?? 0

  const noteCounts = useQuery({
    queryKey: ['note-counts'],
    queryFn: getNoteCounts,
    enabled: unlocked,
    retry: false,
  })
  const pendingFor = (a: AgentRow) =>
    noteCounts.data?.counts?.[`${a.host ?? ''}\0${a.session}`] ?? 0

  // Both are vault-gated: they return `vault_locked` while locked, so don't ask.
  const members = useQuery({
    queryKey: ['group-members'],
    queryFn: getGroupMembers,
    enabled: unlocked && !!groupName,
  })

  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: getProjects,
    // Project Chat is cloud-backed — no machine vault required. The `|| unlocked` half
    // was for the edge lane, deleted 2026-09-02.
  })

  /*
   * WHO IS STUCK WAITING FOR A PERSON. Same key the machine list and the agent sheet
   * already use, so this is a second reader of a warm cache entry, not a new fetch.
   * See agents/blockedAgents.ts for why the reason comes from here rather than from
   * the idle roster's boolean.
   */
  const machinesQ = useQuery({
    queryKey: ['menu-machines'],
    queryFn: getAccountDevices,
    staleTime: 30_000,
  })
  const blocked = useMemo(
    () => blockedByAgent((machinesQ.data as { devices?: AccountDevice[] } | undefined)?.devices),
    [machinesQ.data],
  )

  // Group agents by the machine they are on; the groups are ordered by name below.
  /**
   * Phase 1 search: does this name match what was typed?
   *
   * Case-insensitive substring, nothing cleverer. The field existed for weeks with
   * no handler at all — it looked like a working control and filtered nothing — so
   * the first job is that typing changes the list, not that the matching is smart.
   * Phase 2 (message content) is a different problem: it needs the server, because
   * the client does not hold the transcripts.
   */
  const q = filter.trim().toLowerCase()
  const hit = (...fields: (string | null | undefined)[]) =>
    !q || fields.some((f) => (f ?? '').toLowerCase().includes(q))
  /** Name locally, content from the server. Either qualifies — and the rule is ONE
   *  function because the Pinned section and the machine sections both apply it. */
  const shown = (a: AgentRow) => hit(a.session, a.host) || (!!q && !!contentHits?.has(a.hash))

  /**
   * Does ANY agent have a preview to show?
   *
   * The second line is reserved for every row or for none — reserved per-row would
   * make the list reflow as messages land, and the owner asked for a fixed height.
   * But reserving it before the data exists is a taller list with nothing in it:
   * `last_said_preview` is null for anything the server cannot answer honestly —
   * a guessed transcript, or a non-Claude agent — so many rows have none.
   */

  /**
   * The last preview we saw for each agent, so a TRANSIENT ambiguity does not blank
   * the row.
   *
   * `transcript_ambiguous` means two transcripts were written under one cwd inside
   * a 15-minute window — a subagent sidechain, a second Claude in the same folder, a
   * worktree. It clears on its own when the other one goes quiet, so the honest
   * answer flickers: text, blank, text. avengents-server flagged it and left the
   * call here, which is right; it is a rendering problem, not a data one.
   *
   * Held ONLY for that reason. `nothing_yet`, `no_transcript` and `transcript_stale`
   * are stable answers and a blank is the correct render — sticking there would show
   * words for an agent the server has just said it cannot read.
   */
  /*
   * SEEDED FROM THE DEVICE — owner, 2026-08-22: 每次重刷就不需要重讀一整串了.
   *
   * This was a bare `useRef(new Map())`, so a refresh emptied it and every row lost its
   * second line until a poll had reached each machine. Loading the last page's answers
   * makes the list readable on the first paint; the poll still overwrites every one of
   * them the moment it answers, so nothing here outranks the server.
   */
  const lastSeen = useRef<Map<string, string>>(loadPreviews())
  useEffect(() => {
    let changed = false
    for (const [, a] of idle.byKey) {
      const key = a.hash ?? a.session
      if (a.last_said_preview && lastSeen.current.get(key) !== a.last_said_preview) {
        lastSeen.current.set(key, a.last_said_preview)
        changed = true
      }
    }
    // Only when something actually moved: the poll ticks whether or not anything was
    // said, and rewriting an identical map on every tick is a cost with no reader.
    if (changed) savePreviews(lastSeen.current)
  }, [idle.byKey])

  /**
   * The rail's second line, and WHOSE words it is.
   *
   * WHILE THE AGENT IS WORKING, what it was asked is the line that helps — owner,
   * 2026-09-13: Agent 忙碌並且沒有吐出來新的 Message Block 的時候, 把使用者的最後一個
   * Message 放在這個訊息預覽裡面. A long turn produces no new speech for minutes, so
   * the line that says what the agent SAID goes blank exactly when someone wants to
   * know what it is doing. The ask is shown only while it is working and only when it
   * is the NEWER of the two: once the agent answers, its answer is the news again.
   *
   * `presenceFor` is the pushed busy switch, never the fetched row's `.status` —
   * that one is stale until the next poll, and this whole line would flicker with it.
   */
  const previewFor = (a: AgentRow): Preview => {
    const row = idle.byKey.get(idleKey(a))
    return choosePreview({
      said: row?.last_said_preview,
      saidTs: row?.last_said_ts,
      ask: row?.last_ask_preview,
      askTs: row?.last_ask_ts,
      // The PUSHED switch, never the polled row's `.status`: that one is stale until
      // the next poll, and this line would flicker with it.
      working: presenceFor(idle.byKey, a) === 'working',
      cached: lastSeen.current.get(a.hash) ?? '',
      reason: row?.last_said_reason,
      hasRow: !!row,
    })
  }

  /**
   * Does ANY agent have a preview to show?
   *
   * The second line is reserved for every row or for none — reserved per-row would
   * make the list reflow as messages land, and the owner asked for a fixed height.
   *
   * READ THROUGH `previewFor`, not through the poll. Asking the raw map meant this
   * flipped false the moment a remote host missed a tick, which un-reserved the line
   * on EVERY row at once — the whole list jumped, not just the agent that dropped.
   * That is the "跑掉" half of the reported flicker, and it is why this must be
   * declared after previewFor rather than beside the data it used to read.
   */
  const anyPreview = (agents.data?.agents ?? []).some((a) => !!previewFor(a).text)

  const byHost = new Map<string, AgentRow[]>()
  /*
   * SEEDED FROM THE MACHINES, not from the agents.
   *
   * Grouping by walking the agent list means the only evidence a machine exists
   * is something running on it — so a machine that is paired, live and idle is
   * invisible. The owner paired a second one and saw one: it had nothing running
   * yet, so it produced no group and vanished from a screen whose entire job is
   * to say what this account has.
   *
   * Empty is a state worth drawing. Its absence is indistinguishable from never
   * having asked.
   */
  for (const m of agents.data?.machines ?? []) byHost.set(m.name, [])
  for (const a of agents.data?.agents ?? []) {
    if (!shown(a)) continue
    /*
     * THE MACHINE THE AGENT IS ON.
     *
     * This was `a.host ?? 'Origin'`, which grouped by MOUNTED REMOTE — the shape
     * of a topology with one edge that others were mounted into. Every machine is
     * a paired device now, each holding its own link, so the group is the device.
     * `host` still tells apart a remote mounted into one of them, and the fallback
     * is what it always was when nothing knows better.
     */
    const key = machineNameOf(a, localName)
    const list = byHost.get(key)
    if (list) list.push(a)
    else byHost.set(key, [a])
  }
  for (const list of byHost.values()) list.sort((x, y) => x.session.localeCompare(y.session))

  /* WHICH DEVICE a machine heading names — the Terminal button must open the pty
   * on THAT machine, not wherever the relay happens to point. Seeded from the
   * machines list (covers empty machines); agent stamps fill any name the
   * listing missed. */
  const deviceByHost = new Map<string, string>()
  for (const m of agents.data?.machines ?? []) deviceByHost.set(m.name, m.device_id)
  for (const a of agents.data?.agents ?? []) {
    const k = machineNameOf(a, localName)
    if (a.device_id && !deviceByHost.has(k)) deviceByHost.set(k, a.device_id)
  }

  /*
   * ALWAYS THE SAME PLACE, and the place is the name.
   *
   * This used to put the machine the relay points at first — a sensible thing to
   * want and the wrong thing to sort by. `relayTarget()` is not React state, so
   * opening an agent on another machine did not reorder the rail then; it reordered
   * at whatever unrelated re-render came next, seconds later, with nobody having
   * touched it. A rail that rearranges itself has to be re-read every time instead
   * of learned, and the machine you reach for is never where you last saw it.
   *
   * Sorted through `nameOf`, which is the heading below — so the order is over what
   * a person READS rather than over the map key. The two only part company today for
   * a machine the router itself calls `Origin`; sharing one function is how they are
   * kept from parting company later for a reason nobody predicted.
   *
   * `numeric` so `mbp-2` precedes `mbp-10`; measured, because plain collation puts
   * them the other way round. The locale stays the reader's own — the complaint is
   * that the order MOVES, and one person's collation does not move.
   */
  const nameOf = (host: string) => displayMachineName(host, localName)
  const byName = [...byHost.keys()].sort((a, b) =>
    nameOf(a).localeCompare(nameOf(b), undefined, { numeric: true }),
  )
  /*
   * A NODE OF A SHARED HOME, ONLY WHILE IT RUNS AN AGENT, AND UNDER ITS HOME — owner, 2026-10-22,
   * looking at 1239- … 1244-den-smh100sxm, each "· 0": 我只有在主機上面做連線配對, 為什麼它的 Node 也會
   * 跑到配對的帳號裡面來呢? A node pairs itself (edge nodeEnrol.ts) so that its agents can be reached;
   * with none it is noise beside the machine he paired, and a cluster has one for every job. One
   * that runs agents follows the machine that owns its home, as on Machines (shell/machineNodes.ts),
   * where every node is still listed and can be removed.
   */
  const machineRows = agents.data?.machines ?? []
  const machineById = new Map(machineRows.map((m) => [m.device_id, m]))
  const homeOfNode = new Map<string, string>()
  for (const m of machineRows) {
    const h = m.sibling_of ? machineById.get(m.sibling_of) : undefined
    if (h && h.device_id !== m.device_id && !h.sibling_of && byHost.has(h.name)) homeOfNode.set(m.name, h.name)
  }
  const runsAgents = new Set((agents.data?.agents ?? []).map((a) => machineNameOf(a, localName)))
  const hosts = byName.flatMap((host) =>
    homeOfNode.has(host)
      ? []
      : [host, ...byName.filter((n) => homeOfNode.get(n) === host && runsAgents.has(n))],
  )

  /** Agent-less tmux sessions. Shown so the panel and tmux agree about what exists —
   *  an invisible survivor is how the owner ended up unable to re-create a name that
   *  nothing on screen was holding. */
  const terminals = (agents.data?.terminals ?? []).filter((t) => hit(t.session, t.host))

  /*
   * Delete = end the tmux session completely. Two-tap: the first tap arms THIS row
   * (button turns into "end?"), the second executes; tapping anywhere else or 3s of
   * hesitation disarms. An OS confirm dialog would also work, but it hides WHICH
   * session under a generic prompt — the arm state keeps the name on screen at the
   * moment of decision.
   */
  const [armedKill, setArmedKill] = useState<string | null>(null)
  const armTimer = useRef<number | null>(null)
  const kill = useMutation({
    /*
     * `device` IS NOT OPTIONAL HERE — owner, 2026-08-22: the delete button on a
     * terminal did nothing at all. Without it the POST goes to whichever machine the
     * relay happens to point at, so a terminal on any OTHER machine is asked of an
     * edge that has never heard of it: the edge answers about a session it does not
     * have, the row stays, and the button looks broken.
     *
     * Third door on the same fault. `agentKill`'s own doc names the first two — the
     * agent path (fixed) and everywhere.ts's terminal STAMP (fixed) — and this call
     * site had the stamped `device_id` in scope and dropped it. avg-main verified the
     * edge half kills exactly what it is asked to kill; the question was only ever
     * which edge was asked.
     */
    mutationFn: ({ session, host, device, plain }: { session: string; host: string | null; device?: string; plain?: boolean }) =>
      // A ＋ → Terminal session is ended by its own route, which ends the session and
      // nothing else. Any other: false — this ✕ closes a terminal. It is not the
      // agent-delete action, and a shell has no conversation that can be proven to be its own.
      (plain ? terminalClose(session, device) : agentKill(session, host, device, false)) as Promise<unknown>,
    onSuccess: (_r, v) => {
      setArmedKill(null)
      // Same settle as the card's delete: one refetch can land before the machine
      // has finished ending the session, and nothing polls ['agents'].
      void settleRoster(qc, sessionGone(v.session))
    },
  })
  const killTap = (session: string, host: string | null, device?: string, plain?: boolean) => {
    const key = termKey({ session, host, device_id: device })
    if (armedKill === key) {
      kill.mutate({ session, host, ...(device ? { device } : {}), ...(plain ? { plain } : {}) })
      return
    }
    setArmedKill(key)
    if (armTimer.current !== null) window.clearTimeout(armTimer.current)
    armTimer.current = window.setTimeout(() => setArmedKill(null), 3000)
  }

  /**
   * ONE AGENT ROW, drawn in two places — the guarantee, restated as a TYPE.
   *
   * This was a 280-line closure reading eighteen things from this scope, and its
   * comment argued that a component would mean "a second, drifting list of what a
   * row knows". The premise was right and the conclusion wasn't: `AgentRowCtx` IS
   * that list, typed, built once, and handed to both call sites as one object —
   * the same cannot-drift property, plus a row that can be rendered (and tested)
   * without mounting the whole rail. See AgentRowView.tsx (M6, 拆面板).
   */
  /*
   * ONE CONTEXT UNTIL SOMETHING A ROW SHOWS CHANGES (owner, 2026-11-04: 功能變多了之後 … UI 有時候會變得很卡). Built
   * inline it was new on every render of the rail — and the rail renders with the app — so every row and its pet drew
   * again each time (a dozen components a row, for every agent on the account). Rows are memoised (AgentRowView), so
   * this changes exactly when what they read changes: the data each function closes over is the dependency list, and
   * the app's callbacks are reached through a ref (they are new on every app render; what they DO is the same).
   * `switchRev` is the busy switches: presenceFor reads them from a module map, not from anything here.
   */
  const callbacks = useRef({ onOpenAgent, onOpenCard, onDragSel, isPinned })
  callbacks.current = { onOpenAgent, onOpenCard, onDragSel, isPinned }
  const switchRev = switchRevision()
  const relayNow = relayTarget()
  const rowCtx = useMemo<AgentRowCtx>(
    () => ({
      idle,
      openAgentHash,
      openAgentDevice: openAgentDevice ?? null,
      onDragSel: hasDrag ? (sel) => callbacks.current.onDragSel?.(sel) : null,
      onOpenAgent: (a) => callbacks.current.onOpenAgent(a),
      onOpenCard: hasCard ? (a) => callbacks.current.onOpenCard?.(a) : null,
      pendingFor,
      queuedFor,
      blocked,
      localName,
      q,
      hit,
      contentHits: contentHits ?? null,
      filter,
      anyPreview,
      previewFor,
      myFace,
      isPinned: (a) => callbacks.current.isPinned(a),
      onPin: (a) => setPinSheet({ target: targetOfAgent(a) }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the functions above close over exactly these
    [idle, switchRev, relayNow, openAgentHash, openAgentDevice, hasDrag, hasCard, queues.data, noteCounts.data, blocked, localName, q, contentHits, filter, anyPreview, myFace, pins],
  )
  const agentRow = (a: AgentRow, lead?: ReactNode) => (
    <AgentRowView key={pinKey(a.device_id, a.hash)} ctx={rowCtx} a={a} lead={lead} />
  )

  /*
   * THE PINNED SECTION'S ROWS — a live row where the roster has the agent, and a
   * greyed one where it does not.
   *
   * Matched against the WHOLE roster, then filtered by the same rule the machine
   * sections use, so a search narrows both halves of the rail identically. A pinned
   * agent that is not in the roster is still a row: silently dropping it would make
   * "the machine is off" look like "you never pinned this", and the moment an agent
   * disappears is exactly when the person watching it wants the rail to say so.
   */
  const liveByPin = new Map<string, AgentRow>()
  for (const a of agents.data?.agents ?? []) liveByPin.set(pinKey(a.device_id, a.hash), a)
  const pinnedRows = pins
    .map((p) => ({ p, a: liveByPin.get(pinKey(p.device, p.hash)) }))
    .filter(({ p, a }) => (a ? shown(a) : hit(p.session, p.machine)))
  /** Why a pinned agent has no live row. Same three answers the machine sections
   *  give for an empty block, in the same words, for the same reason: "off", "not
   *  answering" and "still asking" are different sentences. */
  const whyGone = (p: Pin): string => {
    if (agents.isPending) return 'loading…'
    if (agents.isError) return 'couldn’t load agents'
    const why = (agents.data?.failed ?? []).find((f) => f.device_id === p.device)
    if (why) return `couldn’t reach ${why.name}`
    return p.machine ? `not running on ${p.machine}` : 'not running'
  }

  /*
   * ONE SECTION PER TAG — owner, 2026-10-12: 這樣子在釘在最上面的就可以有各種不同的
   * Pinned 的標籤. The rows above, dealt out by the tag each pin is under, in the
   * order the tags stand; a tag with nothing to draw has no section (the rule the
   * one Pinned section already had).
   *
   * VERTICAL DRAG within a section — owner, 2026-08-25: 垂直拖拉移動順序, 跨 Device
   * 同步. While the search filter narrows a section, the drag's indexes are of the
   * rows that are SEEN; the drop is handed on as the two PINS (usePins.moveTo), so
   * it lands relative to what is seen and on the pin that was grabbed.
   */
  const pinnedSections = pinTags
    .map((tag) => ({ tag, rows: pinnedRows.filter(({ p }) => tagOf(p) === tag.id) }))
    .filter(({ rows }) => rows.length > 0)
  const movePinned = (rows: typeof pinnedRows, from: number, to: number) => {
    const src = rows[from]?.p
    const dst = rows[to]?.p
    if (src && dst) movePin(src, dst)
  }

  /*
   * WHILE A FILTER IS TYPED, ONLY WHAT MATCHES — owner, 2026-09-29, making the search
   * Slack's: the field takes the whole bar, and what is under it is about what was
   * typed. Every machine used to keep its heading with "no agents on this machine yet"
   * under it — false: it has agents, none of them match — and the empty-rail card
   * ("Build your crew.") came up under a full account. A section with no match is
   * left out while filtering; a machine that could not be ASKED stays, because the
   * match may be on it. With nothing matching at all, one line says so.
   */
  const failedHosts = new Set((agents.data?.failed ?? []).map((f) => f.name))
  const shownHosts = (scope === 'home' ? hosts : []).filter(
    (host) => !q || (byHost.get(host)?.length ?? 0) > 0 || failedHosts.has(host),
  )
  // A group's chat room is listed under its group in Humans, not here (owner, 2026-10-18).
  const ownProjects = projects.data?.projects.filter((p) => !p.group_id)
  const projectHits = ownProjects?.filter((p) => hit(p.name, p.project_id)) ?? []
  const nothingMatches =
    !!q &&
    scope === 'home' &&
    !agents.isPending &&
    !projects.isPending &&
    shownHosts.length === 0 &&
    projectHits.length === 0 &&
    pinnedRows.length === 0 &&
    terminals.length === 0

  if (only) return <div id="sbList">{only}</div>

  return (
    <div id="sbList" role="tree" aria-label="agents on this tailnet">
      {/* 1. Group — omitted entirely when locked or not in a group. The spec is
             explicit: do not render an empty shell. */}
      {unlocked && groupName && (
        <Section
          id="group"
          label={groupName}
          count={members.data?.members.length}
          isOpen={isOpen('group')}
          onToggle={toggle}
        >
          {members.isPending && <div className="sb-empty">loading members…</div>}
          {members.isError && <div className="sb-empty sb-err">couldn’t load members</div>}
          {[...(members.data?.members ?? [])]
            .filter((m) => hit(m.email))
            .sort((a, b) => a.join_seq - b.join_seq)
            .map((m) => {
              const isSelf = m.email === status?.email
              // DM-ability gate: no chat_key ⇒ a DM would be dead, so the row is inert.
              const dmable = !!m.chat_key && !isSelf
              return (
                <div
                  key={m.device_id}
                  className={
                    `sb-member${dmable ? ' dmable' : ' nokey'}` +
                    (openDmPeer === m.email ? ' active' : '')
                  }
                  role="treeitem"
                  aria-disabled={!dmable}
                  aria-selected={openDmPeer === m.email}
                  tabIndex={dmable ? 0 : -1}
                  /*
                   * THE THIRD KIND. The owner named all three — Agent / Project Chat /
                   * Friend 1-1 Chat — and the first version of the split shipped with
                   * only two of them draggable. `dmable` gates it for the same reason it
                   * gates the click: a row with no secure DM yet has no conversation to
                   * put on the stage.
                   */
                  draggable={!!onDragSel && dmable}
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move'
                    e.dataTransfer.setData('text/plain', m.email)
                    onDragSel?.({ kind: 'dm', peer: m.email })
                  }}
                  onDragEnd={() => onDragSel?.(null)}
                  onClick={() => {
                    if (dmable) onOpenDm(m.email)
                  }}
                  onKeyDown={(e) => {
                    if (dmable && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault()
                      onOpenDm(m.email)
                    }
                  }}
                >
                  <span className="sb-member-name">
                    {isSelf ? `${status?.username ?? m.email} (you)` : m.email}
                  </span>
                  <span className="sb-member-sub">
                    {isSelf || m.chat_key ? `${m.role} · #${m.join_seq}` : 'no secure DM yet'}
                  </span>
                </div>
              )
            })}
        </Section>
      )}

      {/* The Group tab IS the people surface — owner, 2026-08-14: Group Tab 就是把
          user↔user 溝通放進來. Friends live here: add by email, answer requests, tap
          one to open a DM.

          NOT vault-gated — owner, 2026-08-15: 另一個帳號 Vault legacy 機制還在擋路.
          Friends is a Google-session feature served by the BFF (bffJson, session
          cookie); the edge's vault gates EDGE things (terminals), never this. The
          `unlocked` gate here was a leftover from the vault-era group-members section
          this replaced — on an account whose edge reads the legacy vault as locked it
          wrongly hid the whole tab. Session validity is already guaranteed: the app
          only renders the rail when signed in. */}
      {scope === 'group' && (
        <FriendsTab
          selfEmail={status?.email ?? null}
          onOpenDm={onOpenDm}
          {...(onOpenShared ? { onOpenShared } : {})}
          openSharedId={openSharedId}
          {...(dmUnreadByEmail ? { unreadByEmail: dmUnreadByEmail } : {})}
          {...(dmPreviewByEmail ? { previewByEmail: dmPreviewByEmail } : {})}
          openGroupId={openGroupId}
          {...(onGroupOpened ? { onGroupOpened } : {})}
          onOpenGroupChat={(id) => onOpenProject({ project_id: id })}
        />
      )}

      {/* 2. Projects (channels) — on the DynamoDB backend this is a cloud service
          with its own Cognito token, so it does NOT require the machine vault — a
          chat-only account (no paired machine) must still see its projects. The
          `|| unlocked` gate was the vault-era leftover the edge lane needed, and it
          went with that lane on 2026-09-02. */}
      {scope === 'home' && (!q || projectHits.length > 0) && (
        <Section
          id="projects"
          label="Projects"
          count={ownProjects?.length}
          isOpen={isOpen('projects')}
          onToggle={toggle}
        >
          {projects.isPending && <div className="sb-empty">loading…</div>}
          {projects.isError &&
            (!hasChatv1Token() ? (
              /* No chat token yet — the same-origin mint (/chat/token) hasn't produced
                 one. It normally does the moment you're signed in; this is the rare
                 retry, which re-mints from the session and refetches. */
              <button
                type="button"
                className="sb-signin"
                onClick={() => {
                  void refreshChatv1Token().then(() => projects.refetch())
                }}
              >
                Retry loading projects
              </button>
            ) : (
              <div className="sb-empty sb-err">
                couldn’t load projects:{' '}
                {String((projects.error as { code?: string } | undefined)?.code ?? projects.error)}
              </div>
            ))}
          {(() => {
          const rows = projectHits
          /*
           * The latest-message strip is all-or-nothing across the grid.
           *
           * Same rule the agent rail settled on: a card with an empty strip and a card
           * with a filled one are different heights, and a grid of half-empty cards
           * reads as broken rather than as quiet. So the strip appears once ANY project
           * has something to show, and every card reserves it. An edge that does not
           * send the field yet simply has no strip anywhere — which is the truth.
           */
          const anyLast = rows.some((p) => !!p.last_msg_preview)
          return (
          <div className="sb-projgrid">
          {rows.map((p) => {
            const humans = p['member_count'] ?? p['members'] ?? undefined
            const bots = p['agent_count'] ?? p['agents'] ?? undefined
            const last = p.last_msg_preview ?? ''
            // null ⇒ a human wrote it ⇒ THEIR face (photo, else the lettered tile from
            // their address) — the neutral dot only when the row names nobody.
            const lastHash = p.last_from_hash ?? ''
            const lastWho = p.last_from ?? ''
            const lastEmail = p.last_from_email ?? ''
            const lastPicture = p.last_from_picture ?? ''
            return (
              <div
                key={p.project_id}
                className={`sb-proj${openProjectId === p.project_id ? ' active' : ''}`}
                draggable={!!onDragSel}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move'
                  e.dataTransfer.setData('text/plain', p.name ?? p.project_id)
                  onDragSel?.({ kind: 'project', id: p.project_id })
                }}
                onDragEnd={() => onDragSel?.(null)}
                role="treeitem"
                aria-selected={openProjectId === p.project_id}
                onClick={() => onOpenProject(p)}
              >
                <span className="sb-proj-avatar">
                  <ProjectAvatar projectId={p.project_id} label={p.name ?? p.project_id} size={26} />
                  {/* Same corner, same cap, same reasoning as the agent badge above:
                      on the mark it says WHICH project is unread, and "99+" would be
                      wider than the 26px mark it sits on. An edge that does not send
                      the field renders no badge — `?? 0` here would be a claim that
                      there is nothing unread, which is not what a missing field says. */}
                  {(() => {
                    const unread = typeof p.unread === 'number' ? p.unread : 0
                    if (unread <= 0) return null
                    return (
                      <span
                        className="sb-unread"
                        title={`${unread} unread message${unread === 1 ? '' : 's'}`}
                      >
                        {unread > 9 ? '9+' : unread}
                      </span>
                    )
                  })()}
                </span>
                <span className="sb-proj-name">{p.name ?? p.project_id}</span>
                <span className="sb-proj-meta">
                  {typeof humans === 'number' && (
                    <span className="sb-cnt">
                      <Icon name="group" size={11} /> {humans}
                    </span>
                  )}
                  {typeof bots === 'number' && (
                    <span className="sb-cnt">
                      <Icon name="auto_awesome" size={11} /> {bots}
                    </span>
                  )}
                </span>
                {anyLast && (
                  <span className="sb-plast">
                    {/* Who said it, at 16px. A quote with no face is the one thing this
                        strip cannot afford to be: in a room of six agents, "what was
                        said last" is only useful with "by whom" attached to it. */}
                    <LastSpeakerFace hash={lastHash} who={lastWho} email={lastEmail} picture={lastPicture} />
                    <span className="sb-plast-txt">{last}</span>
                  </span>
                )}
              </div>
            )
          })}
          </div>
          )
          })()}
        </Section>
      )}

      {/* 2½. Pinned — owner, 2026-08-24: 介於 Project Chat 以及第一個 Agent Server 的
          中間. The agents this account watches, above every machine, so their status
          is the first thing the rail says — one section per tag since 2026-10-12.
          Omitted entirely with nothing pinned — the pin on each row is the way in,
          and an empty shell would be one more heading to scroll past on a phone.
          Drawn from usePins; the rows come from the same `agentRow` the machines use. */}
      {scope === 'home' &&
        pinnedSections.map(({ tag, rows }) => (
          <PinnedSection
            key={tag.id}
            tag={tag}
            rows={rows}
            isOpen={isOpen(pinSectionId(tag.id))}
            onToggle={toggle}
            agentRow={agentRow}
            whyGone={whyGone}
            onMove={(from, to) => movePinned(rows, from, to)}
            onPin={(target) => setPinSheet({ target })}
            onEdit={() => setPinSheet({ editTag: tag.id })}
          />
        ))}

      {/* 3/4. Hosts: Origin first, then remotes A→Z */}
      {scope === 'home' && agents.isPending && <div className="sb-empty">loading agents…</div>}
      {scope === 'home' && agents.isError && (
        <div className="sb-empty sb-err">couldn’t load agents</div>
      )}

      {nothingMatches && <div className="sb-empty">No agents or projects named “{filter.trim()}”.</div>}

      {shownHosts.map((host) => (
        <Section
          key={host}
          id={host}
          /* The KEY stays `Origin` — collapse state is keyed on it — and only what a
             person reads changes. The order reads this same name, through the same
             `nameOf`, so a heading can never sort under a letter it does not show. */
          label={nameOf(host)}
          kind="machine"
          count={byHost.get(host)?.length}
          isOpen={isOpen(host)}
          onToggle={toggle}
          actions={(() => {
            /* owner, 2026-08-27: 每一台機器的右側增加一個 Terminal 按鈕 — 按下在主
               視窗彈出該機器的 Terminal (非 tmux), 預設顯示 home folder 的路徑. */
            const device = deviceByHost.get(host)
            if (!device || !onOpenMachineShell) return undefined
            return (
              <button
                type="button"
                className="sb-sec-act"
                title={`Terminal on ${nameOf(host)} — a plain shell at its home folder`}
                aria-label={`Open terminal on ${nameOf(host)}`}
                onClick={() => onOpenMachineShell(device, nameOf(host))}
              >
                <Icon name="terminal" size={14} />
              </button>
            )
          })()}
        >
          {/*
            * WHY THIS ONE IS EMPTY.
            *
            * A machine with nothing running and a machine that could not be asked
            * drew the same blank space, so "the Mac has no agents" and "we never
            * got an answer from the Mac" were the same sentence on screen. The
            * fan-out has known the difference since it was written; this is where it
            * stopped being thrown away.
            */}
          {(() => {
            const why = (agents.data?.failed ?? []).find((f) => f.name === host)
            if (why) {
              /*
               * A MACHINE THAT HAS NOT DIALLED IN YET IS NOT AN ERROR.
               *
               * Owner, 2026-09-16, on a machine he had just paired: 這個提示信息應該
               * 改為 connecting your machine, 並且使用灰色的字體會比較不容易造成誤導.
               *
               * Red said something had gone wrong. Nothing had: the edge dials OUT,
               * so between pairing and the first session there is a window where the
               * cloud legitimately has no link to ask — and it is the FIRST thing a
               * new user sees, on the screen where they have just done everything
               * right. (The window used to be up to 30s; server-ts 07329c8 cut the
               * unpaired poll to 2s, which shortens it but cannot remove it.)
               *
               * Only the not-connected reason is softened. Every other detail is a
               * real failure and keeps the red and its own words — "we could not ask"
               * and "we asked and it broke" must not become one sentence, which is
               * the distinction this whole block was written to preserve.
               */
              const dialling = /not connected/i.test(why.detail ?? '')
              return dialling ? (
                <div className="sb-empty" role="status">
                  connecting your machine…
                </div>
              ) : (
                <div className="sb-empty sb-err" role="status">
                  couldn’t list agents — {why.detail}
                </div>
              )
            }
            if ((byHost.get(host)?.length ?? 0) === 0) {
              return <div className="sb-empty">no agents on this machine yet</div>
            }
            /*
             * The roster answered and the STATUS did not. Different sentence on
             * purpose: the agents below are real, but their kind, preview and dot
             * are missing because nobody could be asked — not because there is
             * nothing to say. Without this those rows are indistinguishable from
             * quiet ones.
             */
            const noStatus = idle.failed.find((f) => f.name === host)
            if (noStatus) {
              return (
                <div className="sb-empty sb-err" role="status">
                  listed, but couldn’t read what they’re doing — {noStatus.detail}
                </div>
              )
            }
            return null
          })()}
          {byHost.get(host)?.map((a) => agentRow(a))}
        </Section>
      ))}

      {/* 5. Agent-less tmux sessions. The panel must show everything tmux has —
             the alternative, learned the hard way, is a session that is invisible
             here yet still owns its name at create time. */}
      {scope === 'home' && terminals.length > 0 && (
        <Section
          id="terminals"
          label="Terminals"
          count={terminals.length}
          isOpen={isOpen('terminals')}
          onToggle={toggle}
        >
          {terminals.map((t) => {
            const key = termKey(t)
            const open = () =>
              onOpenAgent({
                session: t.session,
                host: t.host,
                // The stamp is what makes the click open the terminal on the
                // machine the session actually lives on — see everywhere.ts.
                // Spread, not `device_id: t.device_id` — exactOptionalPropertyTypes
                // distinguishes "absent" from "present and undefined", correctly.
                ...(t.device_id ? { device_id: t.device_id } : {}),
                ...(t.device_name ? { device_name: t.device_name } : {}),
                // agentColor string-hashes non-hex input, so the name works.
                hash: t.session,
                onboarded: false,
                cwd: t.cwd ?? null,
              })
            /*
             * NAMED BY ITS FOLDER AND ITS MACHINE — owner, 2026-10-18: Session Name 只需要
             * 給出 Session 的 folder path 以及機器名稱就好了. The tmux name is the machine's
             * handle (term-…), not something a person chose; it stays on the tooltip.
             */
            const folder = t.path ?? t.cwd ?? t.session
            const busy = t.command && !['bash', 'zsh', 'fish', 'sh', 'dash'].includes(t.command) ? t.command : null
            const isOpen = openAgentHash === t.session && (openAgentDevice ?? null) === (t.device_id ?? null)
            return (
              <div
                key={key}
                title={t.cwd ? `${t.session} — ${t.cwd}` : t.session}
                className={`sb-row sb-term${isOpen ? ' active' : ''}`}
                role="treeitem"
                aria-selected={isOpen}
                tabIndex={0}
                onClick={open}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    open()
                  }
                }}
              >
                <span className="sb-pet">
                  <Icon name="terminal" size={18} label="" />
                </span>
                <span className="sb-meta">
                  <span className="sb-name">{folder}</span>
                  <span className="sb-sub">
                    {t.device_name}
                    {busy && (
                      <span className="sb-run">
                        {t.device_name ? ' · ' : ''}running {busy}
                      </span>
                    )}
                  </span>
                </span>
                <button
                  type="button"
                  className={`sb-kill${armedKill === key ? ' armed' : ''}`}
                  title={`Close the terminal in ${folder} — ends its tmux session`}
                  aria-label={`Close terminal ${folder}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    killTap(t.session, t.host, t.device_id, t.plain)
                  }}
                >
                  {armedKill === key ? 'end?' : <Icon name="delete" size={13} label="Close terminal" />}
                </button>
              </div>
            )
          })}
          {kill.error && <div className="sb-empty sb-err">{String(kill.error)}</div>}
        </Section>
      )}

      {/* Last thing in the scroller, so reaching the bottom of the rail is what
          reveals it — and its lead points down-right at the ＋ FAB below. */}
      {!q && <PlusLaunchpad />}

      {/* Portaled to body by Sheet, so where it sits here decides nothing. */}
      {pinSheet && (
        <PinTagSheet
          {...('target' in pinSheet ? { target: pinSheet.target } : { editTag: pinSheet.editTag })}
          onClose={closePinSheet}
        />
      )}
    </div>
  )
}
